import { ResponseCode } from '@/common/request/types';
import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_AVATAR } from '@/modules/avatar/router';
import { useLocale } from '@/modules/locale/model/locale';
import { avatarService } from '@/services/avatar';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { onMounted, reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useAvatarDefaultTranslate() {
    const router = useAppRouter();
    const route = useRoute();
    const { systemLocaleSupported } = useLocale();
    const { executeAction, excuteAndNotify, confirmAndRemove } = useCrudActions();

    const model = reactive({
        id: route.params.avatarId,
        locale: null,
        firstnames: '',
        hasData: false
    });

    const validations = {
        firstnames: { required },
        locale: { required }
    };

    const v$ = useVuelidate(validations, model);

    const beforeFetch = async () => {
        await systemLocaleSupported.load();
        if (!systemLocaleSupported.model.hasData) {
            router.push({ name: ROUTE_NAME_AVATAR.DEFAULT.INDEX });
            return;
        }
    };

    const fetch = executeAction({
        canExecute: () => !StringUtils.isEmpty(model.locale),
        request: () => avatarService.default.firstnames.get(model.id, model.locale),
        onSuccess: ({ data }) => {
            model.hasData = true;
            model.firstnames = data.firstnames;
        },
        onError: ({ code }) => {
            if (code === ResponseCode.NothingFound) {
                model.hasData = false;
                model.firstnames = StringUtils.Characters.Empty;
            } else void router.push({ name: ROUTE_NAME_AVATAR.DEFAULT.INDEX });
        }
    });

    const actions = {
        update: excuteAndNotify({
            validator: v$,
            request: () => avatarService.default.firstnames.update(model.id, model.locale, model.firstnames),
            onSuccess: fetch
        }),
        remove: confirmAndRemove({
            request: () => avatarService.default.firstnames.remove(model.id, model.locale),
            onSuccess: fetch
        })
    };

    onMounted(async () => {
        await beforeFetch();
        await fetch();
    });

    return { model, v$, fetch, actions, systemLocaleSupported };
}
